import React, { useState, useMemo } from 'react';
import './CompetencyBank.css';

/* ─── Static Data ────────────────────────────────────────────────────────────
   Competency categories and a rich seed library relevant to the
   TalentScope360 talent-management context.
   ──────────────────────────────────────────────────────────────────────────── */

const CATEGORIES = [
  { id: 'all',      label: 'All Competencies', icon: 'bi-grid-3x3-gap' },
  { id: 'strategic', label: 'Strategic Outlook',              icon: 'bi-bullseye' },
  { id: 'leadership', label: 'Leadership and Management',       icon: 'bi-person-check' },
  { id: 'interpersonal', label: 'Interpersonal and Communication', icon: 'bi-chat-dots' },
  { id: 'digital',  label: 'Digital and Innovation',            icon: 'bi-cpu' },
  { id: 'operational', label: 'Operational Excellence',       icon: 'bi-gear' },
  { id: 'technical', label: 'Technical and Professional',       icon: 'bi-tools' },
];

const PROFICIENCY_LEVELS = [
  { level: 1, label: 'Novice',      color: '#dc2626', bg: '#fef2f2' },
  { level: 2, label: 'Developing',  color: '#f59e0b', bg: '#fffbeb' },
  { level: 3, label: 'Proficient',  color: '#3b82f6', bg: '#eff6ff' },
  { level: 4, label: 'Advanced',    color: '#8b5cf6', bg: '#f5f3ff' },
  { level: 5, label: 'Expert',      color: '#059669', bg: '#f0fdf4' },
];

const CAT_STYLES = {
  strategic:    { bg: '#fdf4ff', color: '#7e22ce', border: '#e9d5ff' },
  leadership:   { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  interpersonal:{ bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  digital:      { bg: '#f0f9ff', color: '#0369a1', border: '#bae6fd' },
  operational:  { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  technical:    { bg: '#fefce8', color: '#a16207', border: '#fde68a' },
};

const INDUSTRIES = [
  { id: 'agriculture',   label: 'Agriculture',                    icon: 'bi-tree',          color: '#15803d',
    sectors: [
      { id: 'floriculture',   label: 'Floriculture' },
      { id: 'horticulture',   label: 'Horticulture' },
      { id: 'agroforestry',   label: 'Agroforestry' },
      { id: 'animal-farming', label: 'Animal Farming' },
    ],
  },
  { id: 'education',     label: 'Education, Training and Research', icon: 'bi-mortarboard',   color: '#7c3aed',
    sectors: [
      { id: 'cultural-institute',   label: 'Cultural Institute' },
      { id: 'tertiary-institution', label: 'Tertiary Institution' },
      { id: 'private-school',       label: 'Private School' },
      { id: 'professional-college', label: 'Professional College' },
    ],
  },
  { id: 'energy',        label: 'Energy',                         icon: 'bi-lightning',     color: '#d97706',
    sectors: [
      { id: 'energy-extraction',  label: 'Energy Extraction' },
      { id: 'oil-gas-retail',     label: 'Oil and Gas Retail' },
      { id: 'energy-solutions',   label: 'Energy Solutions' },
      { id: 'electricity-retail', label: 'Electricity Retail' },
    ],
  },
  { id: 'financial',     label: 'Financial Services',             icon: 'bi-bank',          color: '#1d4ed8',
    sectors: [
      { id: 'research-institute',     label: 'Research Institute' },
      { id: 'commercial-bank',        label: 'Commercial Bank' },
      { id: 'institutional-investor', label: 'Institutional Investor' },
      { id: 'mfi',                    label: 'Micro-Finance Institution (MFI)' },
      { id: 'sacco',                  label: 'Savings and Credit Organisation (SACCO)' },
    ],
  },
  { id: 'government',    label: 'Government',                     icon: 'bi-building-fill', color: '#0369a1',
    sectors: [
      { id: 'national-government', label: 'National Government' },
      { id: 'county-government',   label: 'County Government' },
      { id: 'government-missions', label: 'Government Missions Abroad' },
      { id: 'saga',                label: 'Semi-Autonomous Government Agency (SAGA)' },
    ],
  },
  { id: 'healthcare',    label: 'Healthcare',                     icon: 'bi-heart',         color: '#dc2626',
    sectors: [
      { id: 'hospital',     label: 'Hospital' },
      { id: 'nursing-home', label: 'Nursing Home' },
    ],
  },
  { id: 'insurance',     label: 'Insurance',                      icon: 'bi-shield-check',  color: '#0891b2',
    sectors: [
      { id: 'insurer',                label: 'Insurer' },
      { id: 'reinsurer',              label: 'Reinsurer' },
      { id: 'insurance-intermediary', label: 'Insurance Intermediary' },
    ],
  },
  { id: 'ict',           label: 'ICT',                            icon: 'bi-cpu',           color: '#6d28d9',
    sectors: [
      { id: 'mobile-network',     label: 'Mobile Network Operator' },
      { id: 'isp',                label: 'Internet Service Provider' },
      { id: 'software-dev',       label: 'Software Development' },
      { id: 'ict-infrastructure', label: 'ICT Infrastructure Provider' },
    ],
  },
  { id: 'media',         label: 'Media',                          icon: 'bi-broadcast',     color: '#db2777',
    sectors: [
      { id: 'advertising-agency',  label: 'Advertising Agency' },
      { id: 'print-broadcast',     label: 'Print and Broadcast House' },
      { id: 'outdoor-advertising', label: 'Outdoor Advertising' },
    ],
  },
  { id: 'logistics',     label: 'Logistics',                      icon: 'bi-truck',         color: '#b45309',
    sectors: [
      { id: 'airline',          label: 'Airline' },
      { id: 'freight-removals', label: 'Freight and Removals' },
      { id: 'rail-operator',    label: 'Rail Operator' },
      { id: 'shipping-line',    label: 'Shipping Line' },
      { id: 'customs-agent',    label: 'Customs Agent' },
      { id: 'cfs',              label: 'Container Freight Station (CFS)' },
      { id: 'warehousing',      label: 'Warehousing' },
      { id: 'ground-handler',   label: 'Ground Handler' },
      { id: 'courier',          label: 'Courier' },
    ],
  },
  { id: 'nonprofit',     label: 'Non-Profit',                     icon: 'bi-people',        color: '#059669',
    sectors: [
      { id: 'global-charity',    label: 'Global Charity' },
      { id: 'local-ngo',         label: 'Local NGO' },
      { id: 'un-organisation',   label: 'UN Organisation' },
      { id: 'international-ngo', label: 'International NGO' },
    ],
  },
  { id: 'retail',        label: 'Retail',                         icon: 'bi-bag',           color: '#e11d48',
    sectors: [
      { id: 'fmcg-retail',         label: 'FMCG Retail Store' },
      { id: 'pharmacy',            label: 'Pharmacy' },
      { id: 'motor-vehicle',       label: 'Motor Vehicle Dealership' },
      { id: 'machinery-equipment', label: 'Machinery and Equipment Retailer' },
      { id: 'general-trade',       label: 'General Trade Retailer' },
    ],
  },
  { id: 'manufacturing', label: 'Manufacturing',                  icon: 'bi-gear',          color: '#475569',
    sectors: [
      { id: 'fmcg-manufacturing',         label: 'FMCG Manufacturing' },
      { id: 'chemical-manufacturing',     label: 'Chemical Manufacturing' },
      { id: 'general-manufacturing',      label: 'General Manufacturing' },
      { id: 'construction-manufacturing', label: 'Construction Manufacturing' },
    ],
  },
  { id: 'tourism',       label: 'Tourism',                        icon: 'bi-airplane',      color: '#f97316',
    sectors: [
      { id: 'hotel',               label: 'Hotel' },
      { id: 'restaurant',          label: 'Restaurant' },
      { id: 'mice-center',         label: 'MICE Center' },
      { id: 'tour-operator',       label: 'Tour Operator' },
      { id: 'tourism-destination', label: 'Tourism Destination' },
    ],
  },
  { id: 'professional',  label: 'Professional Services',          icon: 'bi-briefcase',     color: '#6366f1',
    sectors: [
      { id: 'professional-association', label: 'Professional Association' },
      { id: 'advisory-services',        label: 'Advisory Services' },
      { id: 'realtor',                  label: 'Realtor' },
      { id: 'other-professional',       label: 'Other Professional Services' },
    ],
  },
];

function resolveIndustryLabel(id) {
  const group = INDUSTRIES.find(i => i.id === id);
  if (group) return { label: group.label, color: group.color, icon: group.icon };
  for (const g of INDUSTRIES) {
    const sec = g.sectors.find(s => s.id === id);
    if (sec) return { label: sec.label, color: g.color, icon: g.icon };
  }
  return { label: id, color: '#64748b', icon: 'bi-building' };
}

const COMPETENCY_BANK = [
  /* ── STRATEGIC OUTLOOK ──────────────────────────────────────────────────── */
  {
    id: 'SO-01', name: 'Sensitivity to the Operating Environment', category: 'strategic',
    description: 'The ability to scan, monitor, and interpret the external environment, identifying trends, risks, and opportunities that influence organisational strategy and direction.',
    indicators: [
      'Monitors relevant political, economic, social, and technological developments',
      'Identifies early signals of change and assesses their organisational impact',
      'Uses environmental insights to inform planning and decision-making',
      'Maintains awareness of competitor and sector activity',
    ],
    proficiency: [
      'Aware of key factors in the immediate work environment',
      'Understands how departmental activities connect to the wider operating context',
      'Anticipates how environmental changes may affect departmental plans',
      'Synthesises macro-level trends to inform strategic recommendations',
      'Consistently shapes organisational direction based on deep environmental insight',
    ],
  },
  {
    id: 'SO-02', name: 'Vision and Conceptualisation of Strategy', category: 'strategic',
    description: 'The capacity to define a compelling long-term direction, translate abstract ideas into actionable strategies, and inspire shared commitment to organisational goals.',
    indicators: [
      'Articulates a clear and inspiring vision for the organisation or function',
      'Translates vision into structured, achievable strategic plans',
      'Aligns team activities and resources behind strategic priorities',
      'Communicates strategy in a way that motivates and guides action',
    ],
    proficiency: [
      'Understands the organisational vision and how it applies to own role',
      'Contributes ideas that support strategic plans',
      'Develops functional strategies aligned with organisational direction',
      'Leads strategic planning processes and navigates trade-offs',
      'Sets visionary direction that positions the organisation for long-term success',
    ],
  },
  {
    id: 'SO-03', name: 'Go-getter and Entrepreneurial Drive', category: 'strategic',
    description: 'Demonstrates initiative, resourcefulness, and a results-oriented mindset to identify new opportunities, overcome obstacles, and drive continuous improvement.',
    indicators: [
      'Proactively identifies and pursues opportunities beyond the immediate scope',
      'Takes calculated risks and persists through setbacks',
      'Champions innovation and challenges the status quo constructively',
      'Mobilises resources creatively to achieve results',
    ],
    proficiency: [
      'Completes assigned tasks with enthusiasm and initiative',
      'Seeks additional responsibilities and suggests improvements',
      'Identifies and develops new initiatives to advance team objectives',
      'Leads transformative projects and builds buy-in across the organisation',
      'Cultivates an organisational culture of innovation and enterprise',
    ],
  },

  /* ── LEADERSHIP and MANAGEMENT ────────────────────────────────────────────── */
  {
    id: 'LM-01', name: 'Team Building and Collaboration', category: 'leadership',
    description: 'The ability to foster cohesive, high-performing teams by building trust, leveraging diverse strengths, and creating an inclusive environment that drives collective achievement.',
    indicators: [
      'Builds trust and psychological safety within the team',
      'Recognises and leverages individual team member strengths',
      'Facilitates constructive conflict resolution',
      'Creates opportunities for cross-functional collaboration',
    ],
    proficiency: [
      'Works cooperatively as a reliable team member',
      'Contributes positively to team dynamics and supports peers',
      'Builds and leads cohesive team units with clear goals',
      'Designs team structures and processes that maximise collective performance',
      'Builds organisational capability by modelling and institutionalising collaboration',
    ],
  },
  {
    id: 'LM-02', name: 'People Development and Coaching', category: 'leadership',
    description: 'Commitment to growing the capability of individuals and teams through deliberate development conversations, mentoring, coaching, and creating learning opportunities.',
    indicators: [
      'Identifies individual development needs and co-creates growth plans',
      'Provides timely, constructive feedback tied to performance and potential',
      'Delegates purposefully to stretch and develop team members',
      'Creates an environment where learning from mistakes is valued',
    ],
    proficiency: [
      'Accepts feedback gracefully and applies learning to improve performance',
      'Supports peers through knowledge-sharing and informal mentoring',
      'Conducts regular development conversations and supports individual growth plans',
      'Designs and implements team development programmes aligned to strategy',
      'Builds a talent pipeline and embeds a coaching culture across the organisation',
    ],
  },
  {
    id: 'LM-03', name: 'Decision-Making and Problem Solving', category: 'leadership',
    description: 'The ability to analyse complex situations, weigh evidence and trade-offs, and make sound, timely decisions that advance organisational objectives.',
    indicators: [
      'Gathers and evaluates relevant information before deciding',
      'Makes decisions in a timely manner even under ambiguity',
      'Considers risks, consequences, and stakeholder impact',
      'Learns from outcomes and adjusts future decisions accordingly',
    ],
    proficiency: [
      'Makes routine decisions confidently using available information',
      'Applies structured approaches to solve operational problems',
      'Handles complex, multi-stakeholder decisions with sound judgement',
      'Navigates high-stakes decisions effectively in ambiguous environments',
      'Sets the standard for evidence-based decision culture across the organisation',
    ],
  },
  {
    id: 'LM-04', name: 'Change Management and Adaptability', category: 'leadership',
    description: 'Capacity to lead and navigate organisational change effectively—maintaining productivity, managing resistance, and embedding new ways of working.',
    indicators: [
      'Responds positively and flexibly to new priorities and structures',
      'Communicates the rationale and benefits of change clearly',
      'Supports others through transitions with empathy and clarity',
      'Monitors and adjusts change efforts based on feedback and results',
    ],
    proficiency: [
      'Adapts personal work approach in response to changes',
      'Embraces and champions change within own team',
      'Plans and manages departmental change initiatives',
      'Leads large-scale transformation programmes',
      'Architects organisational change strategy and embeds adaptive cultures',
    ],
  },

  /* ── INTERPERSONAL and COMMUNICATION ─────────────────────────────────────── */
  {
    id: 'IC-01', name: 'Oral and Written Communication', category: 'interpersonal',
    description: 'The ability to convey ideas, information, and insights clearly, concisely, and persuasively across verbal and written channels, tailored to diverse audiences.',
    indicators: [
      'Structures messages logically and adapts tone to audience and purpose',
      'Listens actively and asks clarifying questions',
      'Produces well-written reports, proposals, and correspondence',
      'Delivers confident, engaging presentations',
    ],
    proficiency: [
      'Communicates own work clearly in verbal and written form',
      'Produces clear team reports and facilitates effective meetings',
      'Prepares executive communications and represents the department externally',
      'Shapes organisational narratives and influences senior stakeholders',
      'Leads high-profile communications initiatives; recognised as a thought leader',
    ],
  },
  {
    id: 'IC-02', name: 'Stakeholder Engagement and Relationship Management', category: 'interpersonal',
    description: 'Building and maintaining productive, trust-based relationships with internal and external stakeholders to advance organisational goals and create mutual value.',
    indicators: [
      'Identifies and maps key stakeholders relevant to objectives',
      'Builds genuine relationships through consistent follow-through and reliability',
      'Navigates diverse perspectives and manages expectations proactively',
      'Represents the organisation professionally with external partners',
    ],
    proficiency: [
      'Maintains positive working relationships with colleagues and clients',
      'Manages a defined set of stakeholder relationships effectively',
      'Builds strategic partnerships at department level',
      'Leads complex, multi-party stakeholder landscapes',
      'Shapes the organisation\'s external reputation and partner ecosystem',
    ],
  },
  {
    id: 'IC-03', name: 'Conflict Resolution and Mediation', category: 'interpersonal',
    description: 'The ability to identify, address, and resolve interpersonal or team conflicts constructively, preserving relationships while achieving fair and productive outcomes.',
    indicators: [
      'Recognises early signs of tension and addresses them proactively',
      'Facilitates open, respectful dialogue between parties',
      'Focuses on interests and shared goals rather than positions',
      'Reaches agreements that parties feel are fair and workable',
    ],
    proficiency: [
      'Raises concerns constructively without escalating conflict',
      'Mediates minor disagreements within the team effectively',
      'Leads formal conflict resolution processes',
      'Designs organisational systems to prevent and manage conflict',
      'Recognised authority on conflict management; influences policy and practice',
    ],
  },

  /* ── DIGITAL and INNOVATION ───────────────────────────────────────────────── */
  {
    id: 'DI-01', name: 'Technology Adoption and Systems Thinking', category: 'digital',
    description: 'The ability to embrace, evaluate, and leverage technology to improve efficiency, enable data-driven decisions, and drive organisational innovation.',
    indicators: [
      'Proficiently uses relevant digital tools and platforms',
      'Evaluates new technologies for fit with organisational needs',
      'Understands how systems and processes interconnect',
      'Champions digital adoption and upskilling within the team',
    ],
    proficiency: [
      'Uses standard workplace digital tools effectively',
      'Adopts new tools quickly and supports peers in doing so',
      'Selects and implements departmental technology solutions',
      'Leads digital transformation initiatives across functions',
      'Designs the organisation\'s digital strategy and innovation roadmap',
    ],
  },
  {
    id: 'DI-02', name: 'Data Analytics and Reporting', category: 'digital',
    description: 'Capacity to collect, analyse, interpret, and present data to generate actionable insights that support evidence-based decisions at all levels of the organisation.',
    indicators: [
      'Collects and organises data systematically for analysis',
      'Uses analytical tools to identify patterns and trends',
      'Translates data findings into clear, actionable reports',
      'Validates data quality and challenges assumptions in presented analyses',
    ],
    proficiency: [
      'Accurately records and reports own performance metrics',
      'Analyses operational data to improve team performance',
      'Produces strategic analytics reports for senior decision-makers',
      'Designs organisational data architecture and reporting frameworks',
      'Leads a data-driven culture; recognised externally for analytics excellence',
    ],
  },
  {
    id: 'DI-03', name: 'Creative Thinking and Innovation Management', category: 'digital',
    description: 'The ability to generate novel ideas, challenge existing approaches, and manage the process of turning creative concepts into implemented solutions with measurable impact.',
    indicators: [
      'Generates multiple alternative approaches to problems',
      'Creates psychological safety for idea exploration without premature judgement',
      'Builds prototypes and pilots to test innovations quickly',
      'Scales successful innovations while managing risk',
    ],
    proficiency: [
      'Suggests ideas for improving own work processes',
      'Leads team brainstorming and iterative improvement cycles',
      'Manages a departmental innovation portfolio',
      'Builds innovation pipelines aligned to strategic priorities',
      'Institutionalises innovation as an organisational capability',
    ],
  },

  /* ── OPERATIONAL EXCELLENCE ─────────────────────────────────────────────── */
  {
    id: 'OE-01', name: 'Project Management and Delivery', category: 'operational',
    description: 'The ability to plan, coordinate, execute, and close projects on time, within budget, and to specification—delivering intended outcomes and stakeholder value.',
    indicators: [
      'Defines project scope, objectives, timelines, and resource requirements',
      'Monitors progress against milestones and manages risks proactively',
      'Coordinates cross-functional team activities effectively',
      'Communicates project status clearly to stakeholders',
    ],
    proficiency: [
      'Completes assigned project tasks on time and to standard',
      'Manages small, defined project workstreams independently',
      'Leads moderately complex projects with multiple stakeholders',
      'Manages programmes of interdependent projects with significant impact',
      'Sets project management standards and capability for the organisation',
    ],
  },
  {
    id: 'OE-02', name: 'Results Orientation and Accountability', category: 'operational',
    description: 'A consistent focus on achieving high-quality outcomes, meeting commitments, and taking personal ownership for results—even in challenging circumstances.',
    indicators: [
      'Sets clear, measurable personal and team goals',
      'Takes personal ownership when things go wrong and learns from it',
      'Maintains focus on priorities despite distractions',
      'Holds self and others accountable to agreed standards',
    ],
    proficiency: [
      'Consistently meets own targets and deadlines',
      'Drives team accountability through clear expectations and follow-through',
      'Establishes performance management systems for the department',
      'Creates an organisational culture of ownership and high performance',
      'Recognised as setting industry standards for delivery excellence',
    ],
  },
  {
    id: 'OE-03', name: 'Resource and Financial Management', category: 'operational',
    description: 'Ability to plan, allocate, and steward financial and human resources efficiently to maximise value and maintain fiscal discipline in line with organisational priorities.',
    indicators: [
      'Develops accurate budgets and monitors expenditure against plan',
      'Identifies cost-saving opportunities without compromising quality',
      'Allocates team capacity strategically across priorities',
      'Ensures compliance with financial policies and procurement rules',
    ],
    proficiency: [
      'Manages personal time and assigned budget responsibly',
      'Oversees team budget and resource utilisation effectively',
      'Leads departmental budget planning and resource optimisation',
      'Oversees multi-departmental budget with strategic stewardship',
      'Sets fiscal strategy; accountable for organisational financial performance',
    ],
  },

  /* ── TECHNICAL and PROFESSIONAL ───────────────────────────────────────────── */
  {
    id: 'TP-01', name: 'Professional and Technical Knowledge', category: 'technical',
    description: 'Depth of understanding and application of the specific knowledge, skills, and methodologies required to perform effectively in one\'s professional domain.',
    indicators: [
      'Demonstrates current, accurate knowledge of relevant technical concepts',
      'Applies professional knowledge to solve role-specific challenges',
      'Stays updated on developments in the professional field',
      'Translates technical knowledge into practical outcomes',
    ],
    proficiency: [
      'Applies foundational technical knowledge under supervision',
      'Works independently on routine technical tasks with competence',
      'Resolves complex technical problems and advises peers',
      'Recognised as an internal subject matter expert',
      'Contributes to shaping professional practice at sector or industry level',
    ],
  },
  {
    id: 'TP-02', name: 'Compliance, Ethics and Governance', category: 'technical',
    description: 'Commitment to acting in accordance with legal requirements, ethical principles, and organisational governance standards, while promoting a culture of integrity.',
    indicators: [
      'Understands and adheres to relevant regulatory and policy requirements',
      'Identifies and escalates potential ethical or compliance concerns',
      'Models transparent, honest behaviour in all dealings',
      'Designs controls and checks to ensure governance compliance',
    ],
    proficiency: [
      'Follows organisational policies and procedures consistently',
      'Promotes compliance standards within the team',
      'Leads departmental compliance frameworks and risk management',
      'Designs organisational governance structures and ethics programmes',
      'Recognised externally as a champion of ethical leadership and governance',
    ],
  },
  {
    id: 'TP-03', name: 'Research, Learning and Knowledge Management', category: 'technical',
    description: 'The capacity to systematically acquire, create, organise, and transfer knowledge to support informed decisions, continuous improvement, and organisational learning.',
    indicators: [
      'Conducts structured research and applies findings to work',
      'Documents learnings and shares knowledge proactively',
      'Builds and maintains knowledge repositories for team use',
      'Creates a culture of continuous learning and evidence-based practice',
    ],
    proficiency: [
      'Seeks out information to improve personal performance',
      'Contributes to team knowledge sharing and documentation',
      'Leads research projects and knowledge management systems',
      'Designs organisational learning frameworks and knowledge architecture',
      'Recognised as an intellectual leader; advances knowledge across the sector',
    ],
  },
];

/* ─── Sub-components ────────────────────────────────────────────────────────── */

function CategoryTag({ categoryId }) {
  const s = CAT_STYLES[categoryId] || { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' };
  const cat = CATEGORIES.find(c => c.id === categoryId);
  return (
    <span className="cb-cat-tag" style={{ background: s.bg, color: s.color, borderColor: s.border }}>
      {cat ? cat.label : categoryId}
    </span>
  );
}

function ProficiencyTable({ proficiency }) {
  return (
    <div className="cb-prof-table">
      {PROFICIENCY_LEVELS.map((pl, i) => (
        <div key={pl.level} className="cb-prof-row">
          <div className="cb-prof-level" style={{ background: pl.bg, color: pl.color }}>
            <span className="cb-prof-num">{pl.level}</span>
            <span className="cb-prof-lbl">{pl.label}</span>
          </div>
          <p className="cb-prof-desc">{proficiency[i] || '—'}</p>
        </div>
      ))}
    </div>
  );
}

function CompetencyCard({ comp, isOpen, onToggle }) {
  const catStyle = CAT_STYLES[comp.category] || {};
  const specificIndustries = (comp.industries || ['all']).filter(id => id !== 'all');
  return (
    <div className={`cb-card ${isOpen ? 'open' : ''}`} style={{ borderTopColor: catStyle.color || '#006B54' }}>
      <div className="cb-card-header" onClick={onToggle}>
        <div className="cb-card-main">
          <div className="cb-card-top">
            <span className="cb-code">{comp.id}</span>
            <CategoryTag categoryId={comp.category} />
            {specificIndustries.map(id => {
              const info = resolveIndustryLabel(id);
              return (
                <span key={id} className="cb-industry-tag" style={{ background: `${info.color}18`, color: info.color, borderColor: `${info.color}44` }}>
                  <i className={`bi ${info.icon}`} /> {info.label}
                </span>
              );
            })}
          </div>
          <h3 className="cb-card-name">{comp.name}</h3>
          <p className="cb-card-desc">{comp.description}</p>
        </div>
        <div className="cb-card-meta">
          <span className="cb-ind-count">
            <i className="bi bi-list-check" /> {comp.indicators.length} indicators
          </span>
          <button className="cb-expand-btn" aria-label={isOpen ? 'Collapse' : 'Expand'}>
            <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`} />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="cb-card-body">
          <div className="cb-section">
            <h4 className="cb-section-title"><i className="bi bi-list-check" /> Behavioural Indicators</h4>
            <ul className="cb-indicators">
              {comp.indicators.map((ind, i) => (
                <li key={i} className="cb-indicator-item">
                  <i className="bi bi-check2-circle cb-ind-icon" />
                  <span>{ind}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="cb-section">
            <h4 className="cb-section-title"><i className="bi bi-bar-chart-steps" /> Proficiency Levels</h4>
            <ProficiencyTable proficiency={comp.proficiency} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Add Competency Form ────────────────────────────────────────────────────── */

const EMPTY_COMP = {
  id: '', name: '', category: 'strategic', description: '',
  industries: ['all'],
  indicators: ['', '', '', ''],
  proficiency: ['', '', '', '', ''],
};

function AddCompetencyModal({ onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_COMP);
  const [feedback, setFeedback] = useState({ msg: '', type: '' });

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setInd   = (i, v) => setForm(p => { const a = [...p.indicators]; a[i] = v; return { ...p, indicators: a }; });
  const setProf  = (i, v) => setForm(p => { const a = [...p.proficiency]; a[i] = v; return { ...p, proficiency: a }; });

  const addIndicator    = () => setForm(p => ({ ...p, indicators: [...p.indicators, ''] }));
  const removeIndicator = (i) => setForm(p => ({ ...p, indicators: p.indicators.filter((_, idx) => idx !== i) }));

  const toggleIndustry = (id) => setForm(p => {
    const curr = p.industries.filter(i => i !== 'all');
    const updated = curr.includes(id) ? curr.filter(i => i !== id) : [...curr, id];
    return { ...p, industries: updated.length === 0 ? ['all'] : updated };
  });

  const submit = () => {
    if (!form.id || !form.name || !form.description) {
      setFeedback({ msg: 'Please fill in Code, Name, and Description.', type: 'error' });
      return;
    }
    if (!form.industries.includes('all') && form.industries.length === 0) {
      setFeedback({ msg: 'Please select at least one industry or choose All Industries.', type: 'error' });
      return;
    }
    onSave({
      ...form,
      indicators: form.indicators.filter(Boolean),
      proficiency: form.proficiency,
    });
    setFeedback({ msg: 'Competency added to bank successfully.', type: 'success' });
    setTimeout(onClose, 1000);
  };

  return (
    <div className="cb-overlay" onClick={onClose}>
      <div className="cb-modal" onClick={e => e.stopPropagation()}>
        <div className="cb-modal-header">
          <div>
            <h3 className="cb-modal-title">Add Competency to Bank</h3>
            <p className="cb-modal-sub">Define a new competency with proficiency descriptors and behavioural indicators</p>
          </div>
          <button className="cb-close-btn" onClick={onClose} aria-label="Close"><i className="bi bi-x-lg" /></button>
        </div>

        <div className="cb-modal-body">
          {feedback.msg && (
            <div className={`cb-feedback cb-fb-${feedback.type}`}>{feedback.msg}</div>
          )}

          <div className="cb-form-section">
            <h4 className="cb-form-section-title">Core Information</h4>
            <div className="cb-form-grid">
              <div className="cb-fg">
                <label>Competency Code *</label>
                <input value={form.id} onChange={e => setField('id', e.target.value.toUpperCase())} placeholder="e.g. SO-04" />
              </div>
              <div className="cb-fg">
                <label>Category *</label>
                <select value={form.category} onChange={e => setField('category', e.target.value)}>
                  {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="cb-fg cb-fg-full">
                <label>Competency Name *</label>
                <input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Systems Thinking and Complexity Management" />
              </div>
              <div className="cb-fg cb-fg-full">
                <label>Description *</label>
                <textarea value={form.description} onChange={e => setField('description', e.target.value)} rows={3} placeholder="A concise definition of this competency and its relevance to organisational performance..." />
              </div>
            </div>
          </div>

          <div className="cb-form-section">
            <h4 className="cb-form-section-title">Applicable Industries</h4>
            <div className="cb-industry-select">
              <div className="cb-industry-toggle">
                <button
                  type="button"
                  className={`cb-ind-sel-chip ${form.industries.includes('all') ? 'active' : ''}`}
                  onClick={() => setField('industries', ['all'])}
                >
                  <i className="bi bi-globe" /> All Industries (Cross-Industry)
                </button>
                <button
                  type="button"
                  className={`cb-ind-sel-chip ${!form.industries.includes('all') ? 'active' : ''}`}
                  onClick={() => { if (form.industries.includes('all')) setField('industries', []); }}
                >
                  <i className="bi bi-funnel" /> Industry-Specific
                </button>
              </div>
              {!form.industries.includes('all') && (
                <div className="cb-ind-sel-grid">
                  {INDUSTRIES.map(ind => (
                    <button
                      key={ind.id}
                      type="button"
                      className={`cb-ind-sel-item ${form.industries.includes(ind.id) ? 'selected' : ''}`}
                      style={form.industries.includes(ind.id) ? { borderColor: ind.color, color: ind.color, background: `${ind.color}15` } : {}}
                      onClick={() => toggleIndustry(ind.id)}
                    >
                      <i className={`bi ${ind.icon}`} />
                      <span>{ind.label}</span>
                      {form.industries.includes(ind.id) && (
                        <i className="bi bi-check-circle-fill cb-ind-sel-check" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="cb-form-section">
            <div className="cb-form-section-header">
              <h4 className="cb-form-section-title">Behavioural Indicators</h4>
              <button className="cb-add-row-btn" onClick={addIndicator}>
                <i className="bi bi-plus" /> Add
              </button>
            </div>
            <div className="cb-ind-list">
              {form.indicators.map((ind, i) => (
                <div key={i} className="cb-ind-row">
                  <input
                    value={ind}
                    onChange={e => setInd(i, e.target.value)}
                    placeholder={`Indicator ${i + 1}...`}
                  />
                  {form.indicators.length > 1 && (
                    <button className="cb-remove-btn" onClick={() => removeIndicator(i)} aria-label="Remove">
                      <i className="bi bi-trash3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="cb-form-section">
            <h4 className="cb-form-section-title">Proficiency Level Descriptors</h4>
            <div className="cb-prof-form">
              {PROFICIENCY_LEVELS.map((pl, i) => (
                <div key={pl.level} className="cb-prof-form-row">
                  <div className="cb-prof-level-badge" style={{ background: pl.bg, color: pl.color }}>
                    <span className="cb-prof-num">{pl.level}</span>
                    <span className="cb-prof-lbl">{pl.label}</span>
                  </div>
                  <input
                    value={form.proficiency[i]}
                    onChange={e => setProf(i, e.target.value)}
                    placeholder={`What does Level ${pl.level} look like for this competency?`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="cb-modal-footer">
          <button className="cb-btn-outline" onClick={onClose}>Cancel</button>
          <span style={{ flex: 1 }} />
          <button className="cb-btn-primary" onClick={submit}>
            <i className="bi bi-plus-circle" /> Add to Bank
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────────── */

export default function CompetencyBank() {
  const [competencies, setCompetencies]     = useState(COMPETENCY_BANK);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeIndustry, setActiveIndustry] = useState('all');
  const [activeSector, setActiveSector]     = useState(null);
  const [search, setSearch]                 = useState('');
  const [openId, setOpenId]                 = useState(null);
  const [showAdd, setShowAdd]               = useState(false);

  const filtered = useMemo(() => {
    const activeGroup = INDUSTRIES.find(i => i.id === activeIndustry);
    const groupSectorIds = activeGroup ? activeGroup.sectors.map(s => s.id) : [];

    return competencies.filter(c => {
      const compIndustries = c.industries || ['all'];

      if (activeIndustry !== 'all') {
        const industryMatch =
          compIndustries.includes('all') ||
          compIndustries.includes(activeIndustry) ||
          (activeSector
            ? compIndustries.includes(activeSector)
            : groupSectorIds.some(sid => compIndustries.includes(sid)));
        if (!industryMatch) return false;
      }

      if (activeCategory !== 'all' && c.category !== activeCategory) return false;

      if (search) {
        const q = search.toLowerCase();
        return (
          c.id.toLowerCase().includes(q) ||
          c.name.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.indicators.some(ind => ind.toLowerCase().includes(q))
        );
      }

      return true;
    });
  }, [competencies, activeCategory, activeIndustry, activeSector, search]);

  const countByCategory = useMemo(() => {
    const map = {};
    competencies.forEach(c => { map[c.category] = (map[c.category] || 0) + 1; });
    return map;
  }, [competencies]);

  const toggle = (id) => setOpenId(prev => prev === id ? null : id);

  return (
    <div className="cb-root">

      {/* ── Header ── */}
      <div className="cb-header">
        <div>
          <h1 className="cb-title">Competency Bank</h1>
          <p className="cb-subtitle">
            Master library of organisational competencies with proficiency level descriptors and behavioural indicators
          </p>
        </div>
        <button className="cb-btn-primary" onClick={() => setShowAdd(true)}>
          <i className="bi bi-plus-lg" /> Add Competency
        </button>
      </div>

      {/* ── Industry Filter ── */}
      <div className="cb-industry-section">
        <div className="cb-industry-header">
          <span className="cb-industry-label">
            <i className="bi bi-buildings" /> Industry Context
          </span>
          {activeIndustry !== 'all' && (
            <button
              className="cb-industry-reset"
              onClick={() => { setActiveIndustry('all'); setActiveSector(null); }}
            >
              <i className="bi bi-x-circle" /> Clear Filter
            </button>
          )}
        </div>
        <div className="cb-industry-strip">
          <button
            className={`cb-industry-chip ${activeIndustry === 'all' ? 'active' : ''}`}
            style={activeIndustry === 'all' ? { borderColor: '#006B54', color: '#006B54', background: '#f0f7f4' } : {}}
            onClick={() => { setActiveIndustry('all'); setActiveSector(null); }}
          >
            <i className="bi bi-grid-3x3-gap" />
            <span>All Industries</span>
          </button>
          {INDUSTRIES.map(ind => (
            <button
              key={ind.id}
              className={`cb-industry-chip ${activeIndustry === ind.id ? 'active' : ''}`}
              style={activeIndustry === ind.id ? { borderColor: ind.color, color: ind.color, background: `${ind.color}15` } : {}}
              onClick={() => { setActiveIndustry(ind.id); setActiveSector(null); }}
            >
              <i className={`bi ${ind.icon}`} />
              <span>{ind.label}</span>
            </button>
          ))}
        </div>

        {activeIndustry !== 'all' && (() => {
          const group = INDUSTRIES.find(i => i.id === activeIndustry);
          if (!group) return null;
          return (
            <div className="cb-sector-strip">
              <button
                className={`cb-sector-chip ${activeSector === null ? 'active' : ''}`}
                style={activeSector === null ? { borderColor: group.color, color: group.color, background: `${group.color}15` } : {}}
                onClick={() => setActiveSector(null)}
              >
                All {group.label}
              </button>
              {group.sectors.map(sec => (
                <button
                  key={sec.id}
                  className={`cb-sector-chip ${activeSector === sec.id ? 'active' : ''}`}
                  style={activeSector === sec.id ? { borderColor: group.color, color: group.color, background: `${group.color}15` } : {}}
                  onClick={() => setActiveSector(sec.id)}
                >
                  {sec.label}
                </button>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ── Summary Stats ── */}
      <div className="cb-summary-row">
        <div className="cb-summary-card">
          <i className="bi bi-collection cb-sum-icon" style={{ color: '#006B54' }} />
          <div className="cb-sum-val">{competencies.length}</div>
          <div className="cb-sum-lbl">Total Competencies</div>
        </div>
        {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
          <div key={cat.id} className="cb-summary-card" style={{ borderTopColor: CAT_STYLES[cat.id]?.color }}>
            <i className={`bi ${cat.icon} cb-sum-icon`} style={{ color: CAT_STYLES[cat.id]?.color }} />
            <div className="cb-sum-val" style={{ color: CAT_STYLES[cat.id]?.color }}>{countByCategory[cat.id] || 0}</div>
            <div className="cb-sum-lbl">{cat.label}</div>
          </div>
        ))}
      </div>

      {/* ── Category Tabs ── */}
      <div className="cb-tab-strip">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={`cb-tab ${activeCategory === cat.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            <i className={`bi ${cat.icon}`} />
            <span>{cat.label}</span>
            {cat.id !== 'all' && (
              <span className="cb-tab-count">{countByCategory[cat.id] || 0}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Search ── */}
      <div className="cb-search-wrap">
        <i className="bi bi-search cb-search-icon" />
        <input
          className="cb-search"
          placeholder="Search by code, name, description, or indicator..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className="cb-search-clear" onClick={() => setSearch('')} aria-label="Clear">
            <i className="bi bi-x" />
          </button>
        )}
      </div>

      {/* ── Proficiency Legend ── */}
      <div className="cb-legend">
        <span className="cb-legend-label">Proficiency Scale:</span>
        {PROFICIENCY_LEVELS.map(pl => (
          <span key={pl.level} className="cb-legend-item" style={{ background: pl.bg, color: pl.color }}>
            {pl.level} — {pl.label}
          </span>
        ))}
      </div>

      {/* ── Competency List ── */}
      {filtered.length === 0 ? (
        <div className="cb-empty">
          <i className="bi bi-inbox cb-empty-icon" />
          <div>No competencies match your search.</div>
          <button className="cb-btn-outline" onClick={() => { setSearch(''); setActiveCategory('all'); }}>
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="cb-list">
          {filtered.map(comp => (
            <CompetencyCard
              key={comp.id}
              comp={comp}
              isOpen={openId === comp.id}
              onToggle={() => toggle(comp.id)}
            />
          ))}
        </div>
      )}

      {/* ── Add Modal ── */}
      {showAdd && (
        <AddCompetencyModal
          onClose={() => setShowAdd(false)}
          onSave={c => setCompetencies(prev => [...prev, c])}
        />
      )}
    </div>
  );
}
